import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PiX, PiCircleNotch, PiWarning } from "react-icons/pi";
import { useTranslation } from "react-i18next";
import { cx, initials } from "../utils";

/* ── buttons ─────────────────────────────────────────────────────────── */
export const Button = ({
  variant = "secondary",
  size,
  icon: Icon,
  iconRight: IconRight,
  loading,
  children,
  className,
  ...rest
}) => (
  <button
    type="button"
    {...rest}
    disabled={rest.disabled || loading}
    className={cx("trn-btn", `trn-btn--${variant}`, size && `trn-btn--${size}`, className)}
  >
    {loading ? (
      <PiCircleNotch
        className="animate-spin"
        size={16}
      />
    ) : (
      Icon && <Icon size={size === "lg" ? 20 : 16} />
    )}
    {children}
    {IconRight && !loading && <IconRight size={size === "lg" ? 20 : 16} />}
  </button>
);
export const IconButton = ({ icon: Icon, label, className, ...rest }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    {...rest}
    className={cx("trn-iconbtn", className)}
  >
    <Icon size={18} />
  </button>
);

/* ── pills ───────────────────────────────────────────────────────────── */
export const Pill = ({ tone = "plain", dot = true, children, className, title }) => (
  <span
    title={title}
    className={cx(
      "trn-pill",
      tone !== "plain" && `trn-pill--${tone}`,
      !dot && "trn-pill--plain",
      className
    )}
  >
    {children}
  </span>
);

/* ── avatar ──────────────────────────────────────────────────────────── */
export const Avatar = ({ op, size = 34 }) => (
  <span
    className="trn-avatar"
    style={{
      "--size": `${size}px`,
      "--h": op?.hue ?? 285,
    }}
    aria-hidden="true"
  >
    {initials(op?.first_name, op?.last_name)}
  </span>
);

/* ── bar ─────────────────────────────────────────────────────────────── */
export const Bar = ({ value = 0, tone, className, label }) => (
  <div
    className={cx("trn-bar", tone && `trn-bar--${tone}`, className)}
    role="progressbar"
    aria-valuenow={Math.round(value)}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-label={label}
  >
    <i
      style={{
        "--v": `${Math.min(100, Math.max(0, value))}%`,
      }}
    />
  </div>
);

/* ── tension: 5 notches, filled to `value` (float allowed) ───────────── */
export const TensionDots = ({ value = 0, max = 5, tone = "ink" }) => {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex items-center gap-[3px]"
      role="img"
      aria-label={t("Tension {{value}} sur {{max}}", {
        value: Math.round(value),
        max,
      })}
    >
      {Array.from({
        length: max,
      }).map((_, i) => {
        const fill = Math.min(1, Math.max(0, value - i));
        return (
          <span
            key={i}
            style={{
              width: 5,
              height: 14,
              borderRadius: 2,
              background: `linear-gradient(to top, ${
                tone === "light" ? "oklch(0.86 0.12 80)" : "var(--ink)"
              } ${fill * 100}%, ${
                tone === "light" ? "oklch(1 0 0 / 0.16)" : "var(--rule-strong)"
              } ${fill * 100}%)`,
            }}
          />
        );
      })}
    </span>
  );
};

/* ── count-up ────────────────────────────────────────────────────────── */
export const useCountUp = (target, ms = 1100, start = true) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!start) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setV(target);
      return undefined;
    }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / ms);
      const eased = 1 - (1 - p) ** 4;
      setV(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms, start]);
  return v;
};

/* ── score ring ──────────────────────────────────────────────────────── */
export const ScoreRing = ({
  value = 0,
  max = 100,
  size = 168,
  stroke = 10,
  pass,
  children,
  delay = 0,
}) => {
  const { t } = useTranslation();
  const [on, setOn] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setOn(true), 80 + delay);
    return () => clearTimeout(id);
  }, [delay]);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const frac = Math.min(1, value / max);
  const tone =
    pass == null
      ? "var(--brand)"
      : value >= pass
      ? "var(--good)"
      : value >= pass - 15
      ? "var(--warn)"
      : "var(--bad)";
  return (
    <div
      style={{
        width: size,
        height: size,
        position: "relative",
      }}
      role="img"
      aria-label={t("Score {{value}} sur {{max}}", {
        value,
        max,
      })}
    >
      <svg
        width={size}
        height={size}
        style={{
          transform: "rotate(-90deg)",
        }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--paper-2)"
          strokeWidth={stroke}
        />
        {pass != null && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--rule-strong)"
            strokeWidth={stroke + 6}
            strokeDasharray={`1.5 ${c}`}
            strokeDashoffset={-c * (pass / max)}
          />
        )}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={tone}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={on ? c * (1 - frac) : c}
          style={{
            transition: "stroke-dashoffset 1300ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ── layout bits ─────────────────────────────────────────────────────── */
export const PageHeader = ({ eyebrow, title, subtitle, actions, children }) => (
  <header className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4 pb-6">
    <div className="min-w-0">
      {eyebrow && <div className="t-label mb-2">{eyebrow}</div>}
      <h1 className="t-h1">{title}</h1>
      {subtitle && <p className="t-body mt-2 mb-0">{subtitle}</p>}
      {children}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
  </header>
);
export const SectionHead = ({ title, aside, as: Tag = "h2" }) => (
  <div className="trn-section-head">
    <Tag className="t-h3">{title}</Tag>
    {aside && <div className="t-small">{aside}</div>}
  </div>
);
export const Tabs = ({ tabs, value, onChange, label }) => (
  <div
    className="trn-tabs"
    role="tablist"
    aria-label={label}
  >
    {tabs.map((t) => (
      <button
        key={t.id}
        type="button"
        role="tab"
        className="trn-tab"
        aria-selected={value === t.id}
        onClick={() => onChange(t.id)}
      >
        {t.label}
        {t.count != null && <span className="ml-2 t-muted tnum">{t.count}</span>}
      </button>
    ))}
  </div>
);
export const Empty = ({ title, children, action }) => (
  <div className="trn-empty">
    <div className="t-h3">{title}</div>
    {children && <p className="t-small m-0 max-w-[52ch]">{children}</p>}
    {action}
  </div>
);
export const ErrorState = ({ error, onRetry }) => {
  const { t } = useTranslation();
  return (
    <div className="trn-empty">
      <PiWarning
        size={22}
        color="var(--bad)"
      />
      <div className="t-h3">{t("Impossible de charger cette page")}</div>
      <p className="t-small m-0">{error?.message || t("Une erreur est survenue.")}</p>
      {onRetry && (
        <Button
          variant="secondary"
          onClick={onRetry}
        >
          {t("Réessayer")}
        </Button>
      )}
    </div>
  );
};
export const Skeleton = ({ h = 16, w = "100%", r }) => (
  <div
    className="trn-skel"
    style={{
      height: h,
      width: w,
      borderRadius: r,
    }}
  />
);
export const PageSkeleton = () => {
  const { t } = useTranslation();
  return (
    <div
      className="grid gap-5"
      aria-busy="true"
      aria-label={t("Chargement")}
    >
      <Skeleton
        h={44}
        w="38%"
      />
      <Skeleton
        h={20}
        w="55%"
      />
      <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
        <Skeleton
          h={260}
          r={16}
        />
        <Skeleton
          h={260}
          r={16}
        />
      </div>
      <Skeleton
        h={180}
        r={16}
      />
    </div>
  );
};

/* ── slide-over sheet (a panel, not a modal) ─────────────────────────── */
export const Sheet = ({
  open,
  onClose,
  title,
  subtitle,
  wide,
  footer,
  children,
  labelledBy = "trn-sheet-title",
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const timer = setTimeout(() => ref.current?.focus(), 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(timer);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="trn">
      <div
        className="trn-scrim"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={cx("trn-sheet", wide && "trn-sheet--wide")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        ref={ref}
        tabIndex={-1}
      >
        <div className="trn-sheet__head">
          <div className="min-w-0">
            <h2
              id={labelledBy}
              className="t-h2"
            >
              {title}
            </h2>
            {subtitle && <div className="t-small mt-1">{subtitle}</div>}
          </div>
          <IconButton
            icon={PiX}
            label={t("Fermer")}
            onClick={onClose}
          />
        </div>
        <div className="trn-sheet__body">{children}</div>
        {footer && <div className="trn-sheet__foot">{footer}</div>}
      </aside>
    </div>,
    document.body
  );
};

/* ── form helpers ────────────────────────────────────────────────────── */
export const Field = ({ label, hint, children, htmlFor }) => (
  <div className="trn-field">
    {label && <label htmlFor={htmlFor}>{label}</label>}
    {children}
    {hint && <div className="t-small t-muted">{hint}</div>}
  </div>
);
export const Switch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    style={{
      width: 38,
      height: 22,
      borderRadius: 999,
      border: 0,
      padding: 2,
      cursor: "pointer",
      background: checked ? "var(--brand)" : "var(--rule-strong)",
      transition: "background-color 150ms",
    }}
  >
    <span
      style={{
        display: "block",
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "var(--surface)",
        transform: checked ? "translateX(16px)" : "none",
        transition: "transform 200ms cubic-bezier(0.22,1,0.36,1)",
        boxShadow: "0 1px 2px oklch(0.2 0.05 285 / 0.3)",
      }}
    />
  </button>
);

/* ── confirm via inline toast-ish status ─────────────────────────────── */
export const useFlash = (ms = 2600) => {
  const [msg, setMsg] = useState(null);
  const t = useRef();
  const flash = (m) => {
    setMsg(m);
    clearTimeout(t.current);
    t.current = setTimeout(() => setMsg(null), ms);
  };
  useEffect(() => () => clearTimeout(t.current), []);
  return [msg, flash];
};
